import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  InputSignal,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatIconButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {
  Alert,
  AlertType,
} from '../../models/alert.model';
import {AlertService} from '../../services/alert/alert.service';
import {Constants} from '../../../../common/constants/constants';
import {TranslatePipe} from '@ngx-translate/core';
import {ButtonType} from '../../../../common/enums/button-type.enum';

interface IAlertTypeClasses {
  alert: string;
  icon: string;
}

@Component({
  selector: 'app-alert',
  templateUrl: './alert.component.html',
  styleUrls: ['./alert.component.scss'],
  imports: [MatIconButton, MatIcon, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertComponent implements OnInit {

  protected readonly ButtonType: typeof ButtonType = ButtonType;

  /** How long a toast stays on screen before it dismisses itself. */
  public static readonly DURATION_MS: number = 10 * Constants.ONE_SECOND_IN_MILLISECONDS;

  private static readonly TYPE_CLASSES: { [key in AlertType]?: IAlertTypeClasses } = {
    [AlertType.Success]: {alert: 'alert alert-success', icon: 'check_circle'},
    [AlertType.Error]: {alert: 'alert alert-error', icon: 'error'},
    [AlertType.Info]: {alert: 'alert alert-info', icon: 'info'},
    [AlertType.Warning]: {alert: 'alert alert-warning', icon: 'warning'},
  };

  private alertService: AlertService = inject(AlertService);

  private destroyRef: DestroyRef = inject(DestroyRef);

  /** Outlet id — only alerts published with the same `alertId` are shown here. */
  public id: InputSignal<string> = input<string>();

  /** Currently displayed toasts, newest first. */
  public alerts: WritableSignal<Alert[]> = signal<Alert[]>([]);

  protected readonly AlertType = AlertType;

  ngOnInit(): void {
    this.alertService.onAlert(this.id())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(alert => this.handleAlert(alert));
  }

  /**
   * Method: Show the incoming alert, or clear all toasts when an empty alert
   * (the service's clear signal on route change) arrives. Every alert is
   * shown — even while an identical message is still on screen — so each user
   * action gets its own feedback toast; each one dismisses itself after
   * {@link AlertComponent.DURATION_MS}.
   * @param alert
   */
  private handleAlert(alert: Alert): void {
    if (!alert.message) {
      this.alerts.set([]);
      return;
    }
    this.alerts.update(alerts => [alert, ...alerts]);
    setTimeout(() => this.removeAlert(alert), AlertComponent.DURATION_MS);
  }

  /**
   * Method: Remove the given alert. Removal is by identity, so it is a safe
   * no-op for an alert that is already gone — e.g. the auto-dismiss timer
   * firing after a manual close, or a route-change clear.
   * @param alert
   */
  public removeAlert(alert: Alert): void {
    this.alerts.update(alerts => alerts.filter(item => item !== alert));
  }

  /**
   * Method: Get css class for the alert surface by alert type.
   * @param alert
   */
  public cssClass(alert: Alert): string {
    return AlertComponent.TYPE_CLASSES[alert?.type]?.alert ?? '';
  }

  /**
   * Method: Get the Material icon name for the alert type
   * @param alert - Alert whose type picks the icon
   */
  public getIconName(alert: Alert): string {
    return AlertComponent.TYPE_CLASSES[alert?.type]?.icon ?? '';
  }

}
